/**
 * API call to upload an Astera oncology PA CSV for load-only intake.
 */

import { getBearerToken } from "../../../../api/postCall/bearerToken";
import { API_ENDPOINTS } from "../../../../enums/apiUrls";
import { logDataToConsole, logError } from "../../../../utils/customLogger";

/** Maximum CSV rows accepted by the Astera oncology PA upload endpoint. */
const MAX_ROWS = 500;

export interface OncologyCsvRowResult {
  row_index: number;
  seq?: string | null;
  patient_name?: string | null;
  drug?: string | null;
  status: string;
  identifier?: string | null;
  reason?: string | null;
}

export interface UploadOncologyPaCsvResponse {
  status: "success" | "partial" | "failed";
  filename: string;
  total_rows: number;
  loaded: number;
  skipped: number;
  results: OncologyCsvRowResult[];
}

/**
 * Uploads an Astera oncology PA CSV to the load-only intake endpoint.
 *
 * @param file - The CSV file selected by the user
 * @param orgId - Astera organization id (`tPvNbDprUnrXIJlDXyxs`)
 * @param maxRows - Maximum allowed rows in the CSV (default: 500)
 * @returns Parsed JSON response from the backend
 * @throws {Error} When the request fails or the server returns an error
 */
export const uploadOncologyPaCsv = async (
  file: File,
  orgId: string,
  maxRows: number = MAX_ROWS,
): Promise<UploadOncologyPaCsvResponse> => {
  const token = await getBearerToken();

  if (!token) {
    throw new Error("No token found");
  }

  const formData = new FormData();
  formData.append("file", file, file.name);
  formData.append("org_id", orgId);
  formData.append("max_rows", String(maxRows));

  const response = await fetch(API_ENDPOINTS.UPLOAD_ONCOLOGY_PA_CSV, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (response.status >= 400) {
    const errorText = await response.text();
    logError(new Error(errorText), "Error in uploadOncologyPaCsv");
    throw new Error(errorText);
  }

  const result = (await response.json()) as UploadOncologyPaCsvResponse;
  logDataToConsole("uploadOncologyPaCsv", result);
  return result;
};
